/**
 * Pinia decoration-sets store (Phase 4.8 sets manager + 4.10 editor).
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase 4.8 -- DecorationSetPicker + DecorationSetsListPage +
 *   DecorationSetDetailPage. Phase 4.10 -- DecorationSetEditModal
 *   (placements editor).
 *
 * Wraps the Phase 4.1 CRUD surface at /api/decoration_sets/ (PR #1561).
 * The endpoint contract is documented in
 * ``api/src/endpoints/decoration_sets.py``:
 *
 *   - GET    /decoration_sets/           list (paginated; name, client_tag filters)
 *   - GET    /decoration_sets/<id>/      detail (placements eager-loaded)
 *   - PATCH  /decoration_sets/<id>/      edit metadata (P4.10)
 *   - POST   /decoration_sets/<id>/placements/         add placement (P4.10)
 *   - PATCH  /decoration_sets/<id>/placements/<pid>/   edit placement (P4.10)
 *   - DELETE /decoration_sets/<id>/placements/<pid>/   delete placement (P4.10)
 *   - DELETE /decoration_sets/<id>/            soft-delete (archive) the set
 *   - POST   /decoration_sets/<id>/duplicate/   collision-safe duplicate
 *
 * Tenant model: the backend resolves the acting distributor from the
 * session; True Super Admin (non-impersonating) sees all tenants. The
 * client doesn't tenant-filter on its own.
 *
 * Epoch-guarded the same way ``stores/artworks.js`` and
 * ``stores/clients.js`` are -- ``clear()`` bumps an epoch so a late
 * response from a previous tenant cannot land after impersonation
 * stops.
 *
 * State surface:
 *   - sets, total, page, perPage, loading, error            -- list view
 *   - detail, detailLoading, detailError                    -- detail view
 *   - zones, zonesLoading                                   -- placement zone lookup
 *
 * The list filter params (``filters.name``, ``filters.clientTag``) are
 * tracked on the store so pagination calls reissue the same filter
 * without the page component having to round-trip them.
 *
 * Editor mutation actions (P4.10)
 * -------------------------------
 * ``updateSet`` / ``addPlacement`` / ``updatePlacement`` /
 * ``removePlacement`` / ``reorderPlacements`` are thin wrappers around
 * the backend's editable surfaces. Each one re-hydrates ``detail`` on
 * success so the caller (DecorationSetEditModal + DetailPage) reflects
 * server-authoritative state without an extra round-trip. None of them
 * touch the ``sets`` list cache -- callers that hold a list view should
 * refetch after navigating back.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/services/api'

const DEFAULT_PER_PAGE = 30

export const useDecorationSetsStore = defineStore('decorationSets', () => {
    // ---- list state ----
    const sets = ref([])
    const total = ref(0)
    const page = ref(1)
    const perPage = ref(DEFAULT_PER_PAGE)
    const loading = ref(false)
    const error = ref(null)
    const filters = ref({ name: '', clientTag: null })

    // ---- detail state ----
    const detail = ref(null)
    const detailLoading = ref(false)
    const detailError = ref(null)

    // ---- placement zones lookup (Phase 4.10 editor) ----
    // Hydrated once per session via ``fetchZones``. The zone catalog is
    // a tenant-agnostic lookup table (F3 ``placement_zones``), so we
    // cache it on the store rather than refetching every modal open.
    const zones = ref([])
    const zonesLoading = ref(false)

    // Epoch guard -- mirrors ``stores/artworks.js`` pattern. A bump on
    // ``clear()`` causes any in-flight response to drop on the floor.
    let epoch = 0

    /**
     * Hydrate the paginated list for the effective tenant.
     *
     * @param {Object} opts
     * @param {number} [opts.page]       defaults to current state.page
     * @param {number} [opts.perPage]    defaults to current state.perPage
     * @param {string} [opts.name]       ILIKE substring filter
     * @param {string|null} [opts.clientTag] exact-match filter
     *
     * Empty / null filter values are stripped from the request so the
     * backend doesn't apply an empty-string client_tag exact match
     * (which would only match rows literally tagged "").
     */
    async function fetchList(opts = {}) {
        const myEpoch = epoch
        const nextPage = opts.page != null ? opts.page : page.value
        const nextPerPage = opts.perPage != null ? opts.perPage : perPage.value
        const nextName =
            opts.name !== undefined ? opts.name : filters.value.name
        const nextClientTag =
            opts.clientTag !== undefined
                ? opts.clientTag
                : filters.value.clientTag

        const params = { page: nextPage, per_page: nextPerPage }
        if (typeof nextName === 'string' && nextName.trim()) {
            params.name = nextName.trim()
        }
        if (typeof nextClientTag === 'string' && nextClientTag.trim()) {
            params.client_tag = nextClientTag.trim()
        }

        loading.value = true
        error.value = null
        try {
            const res = await api.get('/decoration_sets/', { params })
            if (myEpoch !== epoch) return
            const body = res?.data || {}
            sets.value = Array.isArray(body.data) ? body.data : []
            total.value = Number.isFinite(body.total) ? body.total : 0
            page.value = Number.isFinite(body.page) ? body.page : nextPage
            perPage.value = Number.isFinite(body.per_page)
                ? body.per_page
                : nextPerPage
            filters.value = {
                name: typeof nextName === 'string' ? nextName : '',
                clientTag:
                    typeof nextClientTag === 'string' ? nextClientTag : null,
            }
        } catch (e) {
            if (myEpoch !== epoch) return
            error.value = e?.message || 'decoration_sets fetch failed'
            sets.value = []
            total.value = 0
        } finally {
            if (myEpoch === epoch) loading.value = false
        }
    }

    /**
     * Hydrate the detail view (placements eager-loaded).
     *
     * Closed-oracle: a 404 from the backend (cross-tenant or missing)
     * surfaces as ``detailError`` with the server-provided message;
     * ``detail`` resets to null so the page renders an empty state.
     */
    async function fetchDetail(id) {
        const myEpoch = epoch
        detailLoading.value = true
        detailError.value = null
        try {
            const res = await api.get(`/decoration_sets/${id}/`)
            if (myEpoch !== epoch) return
            detail.value = res?.data || null
        } catch (e) {
            if (myEpoch !== epoch) return
            detailError.value = e?.message || 'decoration_set fetch failed'
            detail.value = null
        } finally {
            if (myEpoch === epoch) detailLoading.value = false
        }
    }

    /**
     * Duplicate a set via the backend's collision-safe naming endpoint.
     *
     * Returns the newly-created set dict so callers can navigate the
     * router to its detail page without a refetch. The list cache is
     * NOT updated here; callers that hold a list view should refetch
     * after navigating back.
     */
    async function duplicate(id, body = {}) {
        const res = await api.post(`/decoration_sets/${id}/duplicate/`, body)
        return res?.data || null
    }

    /**
     * Soft-delete (archive) a whole set.
     *
     * The backend flips ``is_active=false`` and returns 204 -- no
     * payload. We prune the row from the ``sets`` list cache so a list
     * view drops it immediately without a refetch, and null the detail
     * slot if the archived set was the one being viewed. The set is
     * still restorable server-side via ``?include_archived=1`` (the
     * backend never hard-deletes).
     */
    async function deleteSet(id) {
        await api.delete(`/decoration_sets/${id}/`)
        if (Array.isArray(sets.value)) {
            const next = sets.value.filter(
                (s) => Number(s.id) !== Number(id),
            )
            if (next.length !== sets.value.length) {
                sets.value = next
                if (total.value > 0) total.value -= 1
            }
        }
        if (detail.value && Number(detail.value.id) === Number(id)) {
            detail.value = null
        }
    }

    // ---- Phase 4.10 mutation actions ----

    /**
     * Patch set metadata (name, description, client_tag, is_active).
     *
     * Returns the updated set dict. Re-hydrates ``detail`` if the patched
     * set is the one currently in the detail slot so the page reflects
     * server-authoritative state.
     */
    async function updateSet(id, patch) {
        const res = await api.patch(`/decoration_sets/${id}/`, patch)
        const updated = res?.data || null
        if (updated && detail.value && Number(detail.value.id) === Number(id)) {
            detail.value = updated
        }
        return updated
    }

    /**
     * Add a placement row to an existing set.
     *
     * ``placement`` shape mirrors the backend's
     * ``DecorationSetPlacement`` write surface (placement_zone_id,
     * artwork_id, artwork_version_id, imprint_*, perso_field_keys,
     * sizing_metadata, display_order). All fields optional except the
     * backend's NOT NULL columns (none for placements -- the snapshot
     * may be incomplete; the apply preview surfaces warnings).
     */
    async function addPlacement(setId, placement) {
        const res = await api.post(
            `/decoration_sets/${setId}/placements/`,
            placement || {},
        )
        const created = res?.data || null
        if (
            created
            && detail.value
            && Number(detail.value.id) === Number(setId)
        ) {
            const list = Array.isArray(detail.value.placements)
                ? [...detail.value.placements, created]
                : [created]
            detail.value = {
                ...detail.value,
                placements: list,
                placement_count: list.length,
            }
        }
        return created
    }

    /**
     * Patch an existing placement row (zone, artwork, sizing,
     * display_order, perso_field_keys, etc.).
     *
     * Mirrors the backend's editable-field allowlist. Unknown keys are
     * silently dropped server-side.
     */
    async function updatePlacement(setId, placementId, patch) {
        const res = await api.patch(
            `/decoration_sets/${setId}/placements/${placementId}/`,
            patch || {},
        )
        const updated = res?.data || null
        if (
            updated
            && detail.value
            && Number(detail.value.id) === Number(setId)
            && Array.isArray(detail.value.placements)
        ) {
            detail.value = {
                ...detail.value,
                placements: detail.value.placements.map((p) =>
                    Number(p.id) === Number(placementId) ? updated : p,
                ),
            }
        }
        return updated
    }

    /**
     * Delete a placement row.
     *
     * The backend returns 204; we don't echo a payload. The detail slot
     * is updated locally so the caller doesn't need a separate refetch.
     */
    async function removePlacement(setId, placementId) {
        await api.delete(
            `/decoration_sets/${setId}/placements/${placementId}/`,
        )
        if (
            detail.value
            && Number(detail.value.id) === Number(setId)
            && Array.isArray(detail.value.placements)
        ) {
            const next = detail.value.placements.filter(
                (p) => Number(p.id) !== Number(placementId),
            )
            detail.value = {
                ...detail.value,
                placements: next,
                placement_count: next.length,
            }
        }
    }

    /**
     * Reorder placements by rewriting each row's ``display_order``.
     *
     * ``orderedIds`` is the new sequence of placement ids; the action
     * patches each placement individually with its new index. The
     * backend doesn't expose a bulk reorder endpoint -- the per-row
     * patches are atomic enough for a single-user editor session and
     * mirror how the bulk-apply preview reads display_order.
     *
     * Returns a Promise that resolves after all patches land.
     */
    async function reorderPlacements(setId, orderedIds) {
        if (!Array.isArray(orderedIds)) return
        const tasks = orderedIds.map((pid, idx) =>
            api.patch(
                `/decoration_sets/${setId}/placements/${pid}/`,
                { display_order: idx },
            ),
        )
        await Promise.all(tasks)
        if (
            detail.value
            && Number(detail.value.id) === Number(setId)
            && Array.isArray(detail.value.placements)
        ) {
            // Re-sort the local placements array to match the new order
            // so the page renders the new sequence without a refetch.
            const lookup = new Map(
                detail.value.placements.map((p) => [Number(p.id), p]),
            )
            const reordered = orderedIds
                .map((pid, idx) => {
                    const row = lookup.get(Number(pid))
                    return row ? { ...row, display_order: idx } : null
                })
                .filter((p) => p !== null)
            detail.value = {
                ...detail.value,
                placements: reordered,
            }
        }
    }

    /**
     * Hydrate the placement-zones lookup once per session.
     *
     * Cached on the store so opening the edit modal repeatedly doesn't
     * hit the backend every time. ``clear()`` resets the cache.
     */
    async function fetchZones() {
        if (zones.value.length > 0) return zones.value
        zonesLoading.value = true
        try {
            const res = await api.get('/placement_zones/')
            const body = res?.data || {}
            zones.value = Array.isArray(body.data) ? body.data : []
        } catch (e) {
            // Leave the zones list empty on failure -- the modal renders
            // a "no zones available" empty state rather than crashing.
            zones.value = []
        } finally {
            // Deliberately NOT `finally { return zones.value }` (#3252) --
            // this finally only resets the loading flag, and the return
            // sits outside it so a future throw here would propagate
            // rather than being erased.
            zonesLoading.value = false
        }
        return zones.value
    }

    /**
     * Null out the detail slot (route teardown).
     */
    function clearDetail() {
        detail.value = null
        detailError.value = null
        detailLoading.value = false
    }

    /**
     * Reset both list + detail state. Bumps the epoch so in-flight
     * responses don't repopulate after impersonation stop / logout.
     */
    function clear() {
        epoch += 1
        sets.value = []
        total.value = 0
        page.value = 1
        perPage.value = DEFAULT_PER_PAGE
        loading.value = false
        error.value = null
        filters.value = { name: '', clientTag: null }
        detail.value = null
        detailLoading.value = false
        detailError.value = null
        zones.value = []
        zonesLoading.value = false
    }

    return {
        // state
        sets,
        total,
        page,
        perPage,
        loading,
        error,
        filters,
        detail,
        detailLoading,
        detailError,
        zones,
        zonesLoading,
        // actions
        fetchList,
        fetchDetail,
        duplicate,
        deleteSet,
        updateSet,
        addPlacement,
        updatePlacement,
        removePlacement,
        reorderPlacements,
        fetchZones,
        clearDetail,
        clear,
    }
})
